/**
 * Shared presigned-PUT image upload used by BOTH Account image flows:
 *   • My Profile avatar   → POST /me/profile/upload-url  → PUT /me/profile { avatar }
 *   • Company Profile logo → POST /me/account/logo-upload-url → PATCH /me/account { companyLogo }
 *
 * The server mints the object key from authorized scope; the UI only ever
 * echoes back the returned key. Persistence is the caller's responsibility.
 */

export function uploadFailureMessage(err, { subject = 'image', fallback } = {}) {
  const msg = String(err?.message || '');
  // Browser network errors only (Chrome / Firefox / Safari). Word-bounded so
  // our own "…upload failed (403)" HTTP message is not misreported as CORS.
  if (/Failed to fetch|NetworkError|\bLoad failed\b/i.test(msg)) {
    return `${capitalize(subject)} upload blocked (network/CORS). Confirm this site's origin is allowed on the profile-pictures bucket.`;
  }
  return msg || fallback || `Failed to upload ${subject}`;
}

function capitalize(s) {
  const str = String(s || '');
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * @param {{ requestUploadUrl: (contentType: string) => Promise<{ uploadUrl: string, key: string }>, file: File, subject?: string }} args
 * @returns {Promise<string>} server-minted storage key to persist
 */
export async function uploadImageViaPresignedUrl({ requestUploadUrl, file, subject = 'image' }) {
  const contentType = file?.type || 'image/jpeg';
  const upload = await requestUploadUrl(contentType);
  if (!upload?.uploadUrl || !upload?.key) {
    throw new Error(`Upload URL request for ${subject} returned no key`);
  }
  const putRes = await fetch(upload.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(
      `${capitalize(subject)} upload failed (${putRes.status}). Check profile-pictures bucket CORS for this origin.`,
    );
  }
  return upload.key;
}
